import React, { ChangeEventHandler } from 'react';

import { ICompany } from './models/ICompany';
import { InviteType } from '../src/global/types';

export type TValues = {
  [field: string]: string;
};

export type TLoginValues = {
  email: string;
  password: string;
};

export type TSignUpValues = {
  firstName: string;
  lastName: string;
  email: string;
};

export type TResetPasswordValue = {
  email: string;
};

export type TConfirmPasswordValue = {
  password: string;
  confirmPassword: string;
};

export type TPaymentForm = {
  firstName: string;
  lastName: string;
  email: string;
  hasReadTerms: boolean;
};

export type TAgreementValues = {
  hasReadTerms: boolean;
};

export interface IInputCustom {
  handleBlur: ChangeEventHandler<HTMLInputElement>;
}

export type TNavItem = {
  label: string;
  shortLabel?: string;
  path: string;
  items?: TNavItem[];
};

export type TNavTab = {
  header: string;
  description?: string;
  items: TNavItem[];
};

export type TOption = {
  text: string;
  value: any;
};

export type TClassStatus =
  | 'No Attend'
  | 'Booked'
  | 'Transferred'
  | 'Cancelled'
  | 'Completed'
  | 'Attended'
  | null
  | string;

export type TColor =
  | 'base'
  | 'neutral'
  | 'primary'
  | 'danger'
  | 'green'
  | 'yellow'
  | 'red'
  | 'blue';

export type TClass = {
  id: string;
  sfEventId: string;
  name: string;
  type: 'Public Class' | 'Private Class';
  courseType: CourseType;
  isParent: boolean;
  description: string;
  status: TClassStatus;
  startTime: string;
  endTime: string;
  companyName: string;
  attended: boolean;
  evaluationLink: string;
  isEvaluated: boolean;
  joinLink: string;
  address?: string;
  companyPhoto?: string;
  readingMaterialLink?: string;
  credentials?: {
    roomEmail: string;
    roomPassword: string;
  };
};

export type TLogin = {
  accessToken: string;
  email: string;
  resolvedInvite?: TResolvedInvite;
};

export type TCourseFilters = {
  parentCategories: TNamedItem[];
  categories: TNamedItem[];
  competencies: TNamedItem[];
  stacks: TNamedItem[];
};

export type TActiveFilters = {
  primaryFilter: TNamedItem | null;
  secondaryFilter: TNamedItem | null;
};

export type TNamedItem = {
  id: string;
  name: string;
};

export type TReview = {
  quote: string;
} & TNamedItem;

export type TCoursePreview = {
  id: string;
  productId: string;
  name: string;
  slug: string;
  whoShouldAttend: string;
  priceRange?: string;
  nearestStartTime?: string;
  imageUrl?: string;
  categoryIds: string[];
  skillIds: string[];
  primaryCompetencyIds: string[];
  skillStacks?: string[];
};

export type TCourse = {
  prerequisites: string;
  outline: string;
  skills: TNamedItem[];
  reviews: TReview[];
} & TCoursePreview;

export type TPrivateCourse = {
  availableTime: Omit<TAvailableTime, 'price'>;
  coursePreview: TCoursePreview;
};

export type TPrivateSeries = {
  masterOppId: string;
  name: string;
  coursePreview: TCoursePreview;
  subItems: (Omit<TAvailableTime, 'price'> & { imageUrl?: string })[];
};

export type TPrivateCoursesResponse = {
  courses: TPrivateCourse[];
  series: TPrivateSeries[];
};

export type TCourseGroup = {
  groupName: string;
  courses: TCoursePreview[];
};

export type TInviteType =
  | 'company'
  | 'privateClass'
  | 'classSeries'
  | 'program';

export type TResolvedInvite = {
  inviteType: InviteType;
  itemName: string;
  inviteItemCreated: boolean;
  enforceSaml?: boolean;
  userPresent?: boolean;
  errorCode?: ErrorCode;
};

export type TInviteDetails = {
  company: ICompany | null;
};

export type TInviteRequest = {
  inviteType: TInviteType;
  sfProgramId?: string | null;
  sfLearnitCourseId?: string | null;
  sfSeriesId?: string | null;
};

export type TInviteResponse = {
  resolvedInvite: TResolvedInvite;
};

export type TAvailableModule = {
  id: string;
  name?: string;
  availableTimes: TAvailableTime[];
};

export type TAvailableTime = {
  id: string;
  name: string;
  price: number;
  startTime: string;
  endTime: string;
  isWhitelisted?: boolean;
  isBooked?: boolean;
  isFull?: boolean;
  isWaitlisted?: boolean;
  subItems?: TAvailableTimeSubItem[];
  parentAvailableTime?: Omit<TAvailableTime, 'parentAvailableTime'>;
};

export type TAvailableTimeSubItem = {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
};

export enum CourseType {
  fullDay = 'Full Day',
  cohort = 'Cohort',
  series = 'Series',
  module1 = 'Module 1',
  module2 = 'Module 2',
  module3 = 'Module 3',
  module4 = 'Module 4',
  module5 = 'Module 5',
  module6 = 'Module 6',
}

export type TCalendarEvent = {
  availableTime: TAvailableTime | Omit<TAvailableTime, 'price'>;
  courseInfo: {
    slug?: string;
    name: string;
    type: CourseType;
    imageUrl?: string;
    whoShouldAttend?: string;
    isPrivate: boolean;
  };
};

export type TPublicCartItem = {
  included: boolean;
  studentIds?: string[];
  course: Pick<TCoursePreview, 'name' | 'imageUrl'>;
} & TAvailableTime;

export type TPrivateCartItem = Omit<TPublicCartItem, 'price'>;

export type TCartItem = TPublicCartItem | TPrivateCartItem;

export type TCart = {
  public: TPublicCartItem[];
  private: TPrivateCartItem[];
  processed: TCartItem[];
  status: TCheckoutStatus;
  createdAt: number;
  coupon?: TCoupon;
};

export type TCoupon = {
  code: string;
  percentOff?: number;
  amountOff?: number;
};

export type TCouponValidation = {
  amount: number;
  newAmount: number;
  isValid: boolean;
  metadata: {
    couponUsed: boolean;
    couponCode: string;
    reason: string;
    percentOff?: number;
    amountOff?: number;
  };
};

export type TCheckoutPayment = {
  items: TPublicCartItem[];
  checkoutStudents: TStudent[] | null;
  guestDetails?: {
    email: string;
    firstName: string;
    lastName: string;
  };
  couponCode?: string;
};

export type TInitialCheckoutResult = {
  itemsToPay: TPublicCartItem[];
  processedItems: TCartItem[];
};

export type TStudent = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
};

export type TCheckoutStatus = 'open' | 'second_step' | 'completed';

export type TAssignmentUpdate = { itemId: string; studentId: string };

export type TBaseProgram = {
  id: string;
  type: ProgramType;
  startDate: string;
  endDate: string;
};

export type TProgramInviteValues = {
  sfProgramId: string;
  expiresAt: string;
  maxRegistrations: string;
};

export type TProgramInvite = {
  _id: string;
  token: string;
  maxRegistrations: string;
  registrationCount: string;
  expiresAt: string;
  company: ICompany;
  program: { id: string; name: string; type: ProgramType };
};

export type TProgram = {
  publicEnrollmentEnabled: boolean;
  privateClassesEnabled: boolean;
  message?: {
    headline: string;
    body: string;
  };
} & TBaseProgram;

export enum ProgramType {
  TEAM_PASS = 'Team Pass',
  TEAM_PASS_PLUS = 'Team Pass Plus',
  PARTNERSHIP = 'Partnership',
  PRIVATE_CLASS_SERIES = 'Private Class Series',
  PRIVATE_SINGLE_CLASS = 'Private Single Class',
  PUBLIC_ENROLLMENT = 'Public Open Enrollment',
  LEARNIT_ANYTIME = 'Learnit Anytime',
}

export type TUploadResponse = {
  url: string;
};

export type ImageLoadEvent = React.SyntheticEvent<HTMLImageElement, Event>;

export type ActionMenuItem = {
  label: string;
  onClick?: () => void;
  hidden?: boolean;
  link?: string;
};

export enum ErrorCode {
  EMAIL_TAKEN = 'email_taken',
  LINK_INVALID = 'link_invalid',
  PROGRAM_FULL = 'program_full',
  CLASS_FULL = 'class_full',
  CLASS_ENDED = 'class_ended',
  SERIES_FULL = 'series_full',
  SERIES_ENDED = 'series_ended',
  NAME_REQUIRED = 'name_required',
  NOT_FOUND = 'not_found',
  PERMISSION_DENIED = 'permission_denied',
  UNEXPECTED_ERROR = 'unexpected_error',
  EMAIL_REQUIRED = 'email_required',
}

export type CalendarEventBasicProps = {
  startTimeString: string;
  endTimeString: string;
  type: CourseType;
  isBooked?: boolean;
};

export type CalendarEventLibraryProps = {
  cartItem: TPublicCartItem | TPrivateCartItem;
  slug?: string;
  isPrivate: boolean;
  isClassInCart: boolean;
} & CalendarEventBasicProps;

// ── Ops (staff) · scheduling migration ─────────────────────────────────────

export type TOpsCalendarEvent = {
  id: string;
  kind?: 'reservation' | 'class' | 'block';
  title: string;
  start: string;
  end: string;
  localStart: string | null;
  localEnd: string | null;
  timezone: string | null;
  resourceId: string | null;
  resourceName: string | null;
  resourceType: string | null;
  status: string | null;
  reservationStatus?: string | null;
  holdExpiresAt?: string | null;
  type: string | null;
  hasConflicts: boolean;
  conflictReasons?: string[];
  registrations: { total: number; public: number; private: number };
  maxPlaces: number | null;
  classNumber: string | null;
  productFamily: string | null;
  eventRequestId: string | null;
};

export type TOpsSchedulingMeta = {
  statuses: { id: string; name: string }[];
  types: { id: string; name: string }[];
  resources: { id: string; name: string; typeName: string | null }[];
};

export type TOpsClassListItem = {
  id: string;
  name: string;
  classNumber: string | null;
  classType: string | null;
  status: string | null;
  start: string | null;
  end: string | null;
  timeZone: string | null;
  instructorName: string | null;
  accountName: string | null;
  productFamily: string | null;
  seats: number | null;
  localOrigin: boolean;
  series: TOpsSeriesRef | null;
};

export type TOpsClassList = {
  total: number;
  page: number;
  pageSize: number;
  items: TOpsClassListItem[];
};

export type TOpsClassExceptions = {
  days: number;
  upcoming: number;
  noInstructor: {
    count: number;
    private: number;
    public: number;
    items: Array<{
      id: string;
      name: string;
      classNumber: string | null;
      classType: string | null;
      status: string | null;
      start: string | null;
      accountName: string | null;
      daysOut: number;
      localOrigin: boolean;
    }>;
  };
};

export type TOpsClassDetail = {
  createdDate?: string | null;
  createdBy?: string | null;
  lastModifiedDate?: string | null;
  lastModifiedBy?: string | null;
  programNumber?: string | null;
  programName?: string | null;
  primaryContact?: {
    id: string;
    name: string | null;
    email: string | null;
    phone: string | null;
  } | null;
  onsiteContact?: string | null;
  reasonForSession?: string | null;
  audienceInformation?: string | null;
  recordingDetails?: string | null;
  instructorRequirement?: string | null;
  requestedInstructor?: string | null;
  paymentTerms?: string | null;
  paymentType?: string | null;
  paymentTypeSource?: 'portal' | 'deal' | null;
  prepaidOpportunityId?: string | null;
  fromDeal?: {
    opportunityId: string;
    subType: string | null;
    dealType: string | null;
    learnerRegistrationMethod: string | null;
    ttaOpp: boolean;
    paymentDetail: string | null;
    paymentType: string | null;
    seriesParent: boolean;
    firstInSeries: boolean;
  } | null;
  id: string;
  name: string;
  classType: string | null;
  status: string | null;
  start: string | null;
  end: string | null;
  timeZone: string | null;
  location: string | null;
  locationAddress: string | null;
  videoProvider: string | null;
  productId: string | null;
  productFamily: string | null;
  accountId: string | null;
  accountName: string | null;
  instructorName: string | null;
  seats: number | null;
  cost: number | null;
  clientConfirmed: boolean;
  classNumber: string | null;
  localOrigin: boolean;
  localEditedAt: string | null;
  trainerNotes: string | null;
  paymentNotes: string | null;
  links: TOpsClassLinks;
  series: TOpsSeriesRef | null;
  programId: string | null;
  opportunityId: string | null;
};

export type TOpsClassLinks = {
  learnerLink: string | null;
  privateClassLink: string | null;
  icalLink: string | null;
  studentSurvey: string | null;
  instructorSurvey: string | null;
  courseOutline: string | null;
};

export type TOpsSeriesRef = {
  id: string;
  index: number | null;
  count: number | null;
};

export type TOpsClassUpsert = {
  name: string;
  classType: string;
  status: string;
  start: string;
  end: string;
  timeZone?: string;
  location?: string;
  locationAddress?: string;
  videoProvider?: string;
  productId?: string;
  accountId?: string;
  instructorName?: string;
  seats?: number;
  cost?: number;
  clientConfirmed?: boolean;
  // On a cancel: also email the learners whose seats were open
  notifyLearners?: boolean;
  // Notes and record links; absent = leave alone, '' = clear
  trainerNotes?: string;
  paymentNotes?: string;
  learnerLink?: string;
  privateClassLink?: string;
  studentSurvey?: string;
  instructorSurvey?: string;
  courseOutline?: string;
  reasonForSession?: string;
  audienceInformation?: string;
  recordingDetails?: string;
  instructorRequirement?: string;
  requestedInstructor?: string;
  onsiteContact?: string;
  paymentType?: string;
  paymentTerms?: string;
  prepaidOpportunityId?: string;
};

export type TOpsClassesMeta = {
  products: {
    id: string;
    name: string;
    family: string | null;
    code: string | null;
  }[];
  instructors: string[];
};

export type TOpsAccountHit = {
  id: string;
  name: string;
  type: string | null;
  city: string | null;
};

export type TOpsAssignment = {
  id: string;
  instructorName: string | null;
  courseName: string | null;
  startDate: string | null;
  endDate: string | null;
  eventType: string | null;
  eventStatus: string | null;
  role: string | null;
  classRate: number | null;
  prepRate: number | null;
  contractType: string | null;
  classId: string | null;
  localOrigin: boolean;
  location?: string | null;
  locationAddress?: string | null;
  room?: string | null;
};

export type TOpsAssignmentList = {
  total: number;
  page: number;
  pageSize: number;
  items: TOpsAssignment[];
};

export type TOpsInstructorRates = {
  name: string;
  latestRates: {
    classRate: number | null;
    prepRate: number | null;
    callRate: number | null;
    contractType: string | null;
    asOf: string | null;
  } | null;
};

export type TOpsAssignmentCreate = {
  classId: string;
  instructorName: string;
  classRate?: number;
  prepRate?: number;
  callRate?: number;
  contractType?: string;
};

export type TOpsRegistration = {
  id: string;
  regNumber: string | null;
  learnerName: string | null;
  email: string | null;
  courseName: string | null;
  courseStart: string | null;
  status: string | null;
  category: string | null;
  accountName: string | null;
  bookingDate: string | null;
  contactId: string | null;
  classId: string | null;
  localOrigin: boolean;
  accountId?: string | null;
};

export type TOpsRegistrationList = {
  total: number;
  page: number;
  pageSize: number;
  items: TOpsRegistration[];
};

export type TOpsContactHit = {
  id: string;
  name: string;
  email: string | null;
};

export type TOpsRegistrationCreate = {
  classId: string;
  contactId?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
};

export type TOpsContact = {
  id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  title: string | null;
  accountName: string | null;
  roles: string | null;
  left: boolean;
  localOrigin: boolean;
  matchedOn?: 'name' | 'email' | null;
};

export type TOpsContactList = {
  total: number;
  page: number;
  pageSize: number;
  items: TOpsContact[];
};

export type TOpsContactDetail = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  mobilePhone: string | null;
  title: string | null;
  accountId: string | null;
  accountName: string | null;
  roles: string | null;
  left: boolean;
  localOrigin: boolean;
  localEditedAt: string | null;
};

export type TOpsContactUpsert = {
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  mobilePhone?: string;
  title?: string;
  accountId?: string;
  roles?: string;
  left?: boolean;
};

export type TOpsProduct = {
  id: string;
  name: string | null;
  family: string | null;
  code: string | null;
  active: boolean;
  status: string | null;
  websiteReady: boolean;
  description: string | null;
  classCount: number;
  localOrigin: boolean;
  /** The portal's own list price, when one is set. */
  listPrice: number | null;
  /** The Salesforce Public Price Book entry, when one exists. */
  bookPrice: number | null;
  /** What the learner catalogue charges: listPrice, else bookPrice. */
  price: number | null;
};

export type TOpsProductList = {
  total: number;
  page: number;
  pageSize: number;
  items: TOpsProduct[];
};

export type TOpsProductUpsert = {
  name: string;
  family?: string;
  code?: string;
  active?: boolean;
  websiteReady?: boolean;
  description?: string;
  listPrice?: number | null;
};

export type TOpsProgram = {
  id: string;
  name: string | null;
  number: string | null;
  accountName: string | null;
  status: string | null;
  startDate: string | null;
  endDate: string | null;
  subtype: string | null;
  teamPass: boolean;
  invested: number | null;
  used: number | null;
  remaining: number | null;
  memberCount: number | null;
  renewed: boolean;
  ledgerMode: TOpsLedgerMode;
  localOrigin: boolean;
};

export type TOpsLedgerMode = 'dry-run' | 'live';

export type TOpsProgramList = {
  total: number;
  page: number;
  pageSize: number;
  items: TOpsProgram[];
};

export type TOpsProgramMember = {
  id: string;
  contactId: string | null;
  name: string | null;
  email: string | null;
  status: string | null;
  enabled: boolean;
  classesInWindow: number;
  localOrigin: boolean;
};

export type TOpsProgramDetail = Omit<TOpsProgram, 'memberCount'> & {
  publicDiscount: number | null;
  privateDiscount: number | null;
  allowPublicEnrollment: boolean;
  maxMembers: number | null;
  /** What Salesforce last computed as invested — the baseline. */
  investedSalesforce: number | null;
  /** Funds added on top of the original amount (live top-up rows; 0 in dry-run). */
  topUps: number;
  /** What Salesforce last computed — the baseline the portal builds on. */
  usedSalesforce: number | null;
  /** Net of the portal's live ledger rows (0 in dry-run). */
  usedPortal: number;
  /** Team Pass tally kept by the portal. */
  valueConsumed: number;
  /** Rehearsed deductions not applied to any balance. */
  pendingDryRun: number;
  members: TOpsProgramMember[];
};

export type TOpsCase = {
  id: string;
  caseNumber: string | null;
  subject: string | null;
  status: string | null;
  origin: string | null;
  reason: string | null;
  createdDate: string | null;
  contactName: string | null;
  closed: boolean;
  timeInStageHours: number | null;
  localOrigin: boolean;
};

export type TOpsCaseList = {
  total: number;
  page: number;
  pageSize: number;
  items: TOpsCase[];
};

export type TOpsCaseDetail = TOpsCase & {
  description: string | null;
  contactEmail: string | null;
  accountName: string | null;
};

export type TOpsAccountRow = {
  id: string;
  name: string | null;
  type: string | null;
  industry: string | null;
  tier: string | null;
  website: string | null;
  livePrograms: number;
  upcomingClasses: number;
};

export type TOpsAccountList = {
  total: number;
  page: number;
  pageSize: number;
  items: TOpsAccountRow[];
};

export type TOpsAccount360 = {
  accountOwner?: {
    name: string | null;
    email: string | null;
    basis: string;
    asOf: string | null;
  } | null;
  id: string;
  name: string | null;
  type: string | null;
  industry: string | null;
  tier: string | null;
  phone: string | null;
  website: string | null;
  // "Don't send my people anything" — automated learner mail is held
  commsOptOut: boolean;
  programs: {
    id: string;
    name: string | null;
    status: string | null;
    startDate: string | null;
    endDate: string | null;
    invested: number | null;
    remaining: number | null;
    members: number | null;
    number?: string | null;
    contractPoc?: string | null;
  }[];

  upcomingClasses: {
    id: string;
    name: string | null;
    start: string | null;
    status: string | null;
    instructor: string | null;
    type: string | null;
  }[];
  recentRegistrations: {
    id: string;
    learner: string | null;
    course: string | null;
    start: string | null;
    status: string | null;
  }[];
  openCases: {
    id: string;
    caseNumber: string | null;
    subject: string | null;
    status: string | null;
    createdDate: string | null;
  }[];
  contactCount: number;
  contacts: {
    id: string;
    name: string | null;
    email: string | null;
    title: string | null;
  }[];
  closedCases?: {
    id: string;
    caseNumber: string | null;
    subject: string | null;
    status: string | null;
    createdDate: string | null;
  }[];
  classCounts?: { upcoming: number; past: number };
};

export type TOpsAccountUpdate = {
  name: string;
  type?: string;
  industry?: string;
  phone?: string;
  website?: string;
  commsOptOut?: boolean;
};

export type TOpsTemplate = {
  id: string;
  name: string | null;
  subject: string | null;
  timesUsed: number;
  lastUsed: string | null;
};

export type TOpsTemplateList = {
  total: number;
  page: number;
  pageSize: number;
  items: TOpsTemplate[];
};

export type TOpsTemplateRender = {
  id: string;
  name: string | null;
  renderedSubject: string;
  renderedHtml: string;
  isHtml: boolean;
  mergeFields: string[];
  resolvedCount: number;
  sampleRecord: { object: string; id: string; label: string } | null;
};

export type TOpsCommsStatus = {
  mode: 'dry-run' | 'live';
  transport: 'graph' | 'held';
  configured: boolean;
  from: string;
  redirectTo: string | null;
  allowlist: string[];
  missing: string[];
};

/** Why a message is being sent — anything but `manual` honours a quiet account. */
export type TOpsMessageCategory =
  | 'manual'
  | 'reminder'
  | 'confirmation'
  | 'cancellation'
  | 'reschedule'
  | 'program-welcome'
  | 'prep-call'
  | 'travel'
  | 'kickoff'
  | 'post-class'
  | 'internal';

export type TOpsQueueBody = {
  templateId: string;
  recordId?: string;
  to: string[];
  cc?: string[];
  bcc?: string[];
  internalCopy?: boolean;
  spawnCase?: boolean;
  category?: TOpsMessageCategory;
};

export type TOpsOutboxStatus =
  | 'held'
  | 'sent'
  | 'failed'
  | 'suppressed'
  | 'cancelled';

export type TOpsQueueResult = {
  id: string;
  status: TOpsOutboxStatus;
  holdReason: string | null;
  error: string | null;
  caseId: string | null;
  quietAccount: boolean;
  unresolvedFields: number;
};

export type TOpsOutboxAction = {
  id: string;
  status: string;
  holdReason: string | null;
  error?: string | null;
};

export type TOpsOutboxItem = {
  id: string;
  from: string | null;
  to: string[];
  cc: string[];
  bccCount: number;
  subject: string | null;
  status: string | null;
  holdReason: string | null;
  error: string | null;
  category: TOpsMessageCategory;
  queuedAt: string | null;
  sentAt: string | null;
  templateName: string | null;
  recordId: string | null;
  recordObject: string | null;
  caseId: string | null;
  actorEmail: string | null;
  redirectedTo: string | null;
};

export type TOpsOutboxList = {
  total: number;
  page: number;
  pageSize: number;
  byStatus: Record<string, number>;
  items: TOpsOutboxItem[];
};

export type TOpsEval = {
  id: string;
  date: string | null;
  course: string | null;
  account: string | null;
  rating: number | null;
  recommend: number | null;
  takeaway: string | null;
  localOrigin: boolean;
};

export type TOpsEvalOverview = {
  total: number;
  page: number;
  pageSize: number;
  avgRating: number | null;
  avgRecommend: number | null;
  byYear: { year: string; n: number }[];
  items: TOpsEval[];
};

export type TOpsAccountEvals = {
  count: number;
  avgRating: number | null;
  avgRecommend: number | null;
  recentTakeaways: {
    course: string | null;
    date: string | null;
    text: string;
  }[];
};

export type TOpsClassRelated = {
  assignments: {
    id: string;
    instructor: string | null;
    role: string | null;
    status: string | null;
    contractType: string | null;
    localOrigin: boolean;
  }[];
  registrationCount: number;
  evals: {
    count: number;
    avgRating: number | null;
    recentTakeaways: { date: string | null; text: string }[];
  };
  cases: {
    id: string;
    subject: string | null;
    status: string | null;
    createdDate: string | null;
    closed: boolean;
  }[];
};
